import { runSiteMutation, runSiteQuery } from '@/common/site-details/site.query';
import { isUnauthorizedError } from '@/pages/shared/rtk-query-helpers';
import { createAction, createSlice, current } from '@reduxjs/toolkit';
import { changeTableRow, hidePanel, clearFilter, setFilter, updateTable } from '@/pages/shared/shared.reducer';
import { getUserPermissions, getSetting } from '@/pages/login/login.reducer';
import { openSuccessNotification, openErrorNotification } from '@/notification';
import { CHANGES_ARE_SAVED } from '@/constants/constant.string';
import { b64toBlob } from '@/helpers/csv-save-helper';
import { config } from '@/config';
import FileSaver from 'file-saver';
import { dashboardApi } from '@/pages/dashboard/dashboard.reducer'

const getRoleName = (roles, roleId, fallbackRoleName = null) => {
    if (roleId == null) {
        return null;
    }

    return roles.find(item => item.roleId == roleId)?.role ?? fallbackRoleName;
}

const buildUserRoleRow = (item, roleId, roleName) => ({
    ...item,
    roleId,
    roleName,
    role: roleName,
    rolename: roleName,
    hasAccess: roleId != null
});

const getApiErrorMessage = (error, fallbackMessage) => {
    const responseMessage = error?.data?.message ?? error?.response?.data?.message;
    const directMessage = error?.message;

    if (responseMessage && !/undefined/i.test(responseMessage)) {
        return responseMessage;
    }

    if (directMessage && !/undefined/i.test(directMessage) && !/^request failed/i.test(directMessage)) {
        return directMessage;
    }

    return fallbackMessage;
};

const SITE_DETAILS_VALUE_SET = "SITE_DETAILS_VALUE_SET";
const SITE_VALUE_CHANGE = 'SITE_VALUE_CHANGE';
const CLEAR_SITE_DETAILS = 'CLEAR_SITE_DETAILS';
const SITE_HISTORY_SET = 'SITE_HISTORY_SET';

const initialState = {
    site: { country: {countryId: 1, country: 'Australia'}, isShared: true, watermark: true, watermarkSuperceded: 'Superseded', watermarkUnapproved: 'Unapproved', watermarkUncontrolled: 'Uncontrolled', watermarkRetired: 'Retired', watermarkUnderchange: 'Underchange', watermarkDrafts: 'Draft', tagFilters: '' },
    editSite: { country: {countryId: 1, country: 'Australia'}, isShared: true, watermark: true, watermarkSuperceded: 'Superseded', watermarkUnapproved: 'Unapproved', watermarkUncontrolled: 'Uncontrolled', watermarkRetired: 'Retired', watermarkUnderchange: 'Underchange', watermarkDrafts: 'Draft', tagFilters: '' },
    siteDetailsLoading: false,
    siteSystemContacts: [],
    accountSites: [],
    selectedSite: {id: 0, name: ''},
}

// Legacy action type strings are preserved so existing dispatch sites keep working.
export const changeSiteValue = createAction(SITE_VALUE_CHANGE, (key, value) => ({
    payload: { key, value }
}));

export const setValue = createAction(SITE_DETAILS_VALUE_SET, (key, value) => ({
    payload: { key, value }
}));

export const setHistory = createAction(SITE_HISTORY_SET, (history) => ({
    payload: { history }
}));

export const clearSiteDetails = createAction(CLEAR_SITE_DETAILS);

const siteSlice = createSlice({
    name: 'siteDetails',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(setValue, (draft, action) => {
                draft[action.payload.key] = action.payload.value;
            })
            .addCase(changeSiteValue, (draft, action) => {
                draft.editSite[action.payload.key] = action.payload.value;
            })
            .addCase(setHistory, (draft, action) => {
                draft.site.logs = [...action.payload.history];
                draft.editSite.logs = [...action.payload.history];
            })
            .addCase(clearSiteDetails, (draft) => ({
                ...initialState,
                accountSites: current(draft).accountSites
            }));
    }
});

const siteReducer = siteSlice.reducer;

export const getSiteDetails = (id) => {
    return (dispatch) => {
        dispatch(setValue('siteDetailsLoading', true));
        runSiteQuery(dispatch, 'getSiteDetails', id)
            .then(data => {
                dispatch(setValue('site', data));
                dispatch(setValue('editSite', data));
            })
            .catch(error => {
                if (!isUnauthorizedError(error)) {
                    openErrorNotification("Can't load site details. Please reload page")
                }
            })
            .finally(() => {
                setTimeout(() => {
                    dispatch(setValue('siteDetailsLoading', false));
                }, 200);
            })
    }
}

export const deleteSite = (item) => {
    return (dispatch) => {
        dispatch(setValue('siteDetailsLoading', true));

        runSiteMutation(dispatch, 'deleteSite', item.siteId)
            .then(async (data) => {
                openSuccessNotification(`Site ${item.site} is deleted`);
                dispatch(setValue('site', data));
                dispatch(setValue('editSite', data));
                dispatch(changeTableRow(`accountUsersSitesTable${item.accountId}`, { ...data }, 'siteId'));
                dispatch(setFilter(`accountUsersSitesTable${item.accountId}`, 'key', Math.floor(Math.random() * Math.floor(2500))));
                dispatch(updateTable(`accountUsersSitesTable${item.accountId}`));
                dispatch(getUserPermissions());
                await dispatch(dashboardApi.endpoints.fetchSiteList.initiate(undefined, { forceRefetch: true })).unwrap();
            })
            .catch(error => {
                if (!isUnauthorizedError(error)) {
                    openErrorNotification(`Error. Can't delete site ${item.site}`);
                }
            })
            .finally(() => {
                setTimeout(() => {
                    dispatch(setValue('siteDetailsLoading', false));
                }, config.LOADING_DELAY);
            })
    }
}

export const restoreSite = (accountId, siteId) => {
    return (dispatch) => {
        dispatch(setValue('siteDetailsLoading', true));

        runSiteMutation(dispatch, 'restoreSite', siteId)
            .then(async (data) => {
                openSuccessNotification('Site is restored');
                dispatch(setValue('site', data));
                dispatch(setValue('editSite', data));
                dispatch(changeTableRow(`accountUsersSitesTable${accountId}`, { ...data }, 'siteId'));
                dispatch(setFilter(`accountUsersSitesTable${accountId}`, 'key', Math.floor(Math.random() * Math.floor(2500))));
                dispatch(updateTable(`accountUsersSitesTable${accountId}`));
                dispatch(getUserPermissions());
                await dispatch(dashboardApi.endpoints.fetchSiteList.initiate(undefined, { forceRefetch: true })).unwrap();
            })
            .catch(error => {
                if (!isUnauthorizedError(error)) {
                    openErrorNotification("Some problem. Can't restore site");
                }
            })
            .finally(() => {
                setTimeout(() => {
                    dispatch(setValue('siteDetailsLoading', false));
                }, config.LOADING_DELAY);
            })
    }
}

export const saveSiteChanges = (site, needClose) => {
    return (dispatch, store) => {
        dispatch(setValue('siteDetailsLoading', true));

        runSiteMutation(dispatch, 'saveSite', site)
            .then(async (data) => {
                if (!site.siteId) {
                    dispatch(getUserPermissions());
                }

                if (data.site.isShared != store().siteDetails.site.isShared) {
                    dispatch(clearFilter(`siteUsersTable-${site.siteId}`));
                }

                openSuccessNotification(CHANGES_ARE_SAVED);
                dispatch(setFilter(`accountUsersSitesTable${site.accountId}`, 'key', Math.floor(Math.random() * Math.floor(2500))))

                if (needClose) {
                    dispatch(hidePanel('sitesPanel'));
                }
                else {
                    dispatch(setValue('site', data.site));
                    dispatch(setValue('editSite', data.site));
                    dispatch(setValue('selectedSite', { id: data.site.siteId, name: data.site.site }));
                }
                if (data.file) {
                    var blob = b64toBlob(data.file, "application/octet-stream");
                    FileSaver.saveAs(blob, 'RemovedCollectionsReport.csv');
                }
                await dispatch(dashboardApi.endpoints.fetchSiteList.initiate(undefined, { forceRefetch: true })).unwrap();
            })
            .catch(error => {
                if (!isUnauthorizedError(error)) {
                    openErrorNotification(`Error. Can't save site changes`);
                }
            })
            .finally(async () => {
                dispatch(getUserPermissions());
                await dispatch(dashboardApi.endpoints.fetchSiteList.initiate()).unwrap();

                setTimeout(() => {
                    dispatch(setValue('siteDetailsLoading', false));
                }, config.LOADING_DELAY);
            })
    }
}

export const changeUserRole = (model, item, isProfile, table) => {
    return async (dispatch, getState) => {
        dispatch(setValue('siteDetailsLoading', true));

        const roles = getState().shared.roles || [];
        const previousRoleName = item.roleName ?? item.role ?? item.rolename ?? null;
        const previousRow = buildUserRoleRow(item, item.roleId ?? null, previousRoleName);
        const optimisticRoleName = getRoleName(roles, model.roleId, previousRoleName);
        const optimisticRow = buildUserRoleRow(item, model.roleId ?? null, optimisticRoleName);

        dispatch(changeTableRow(table, optimisticRow, 'userId'));

        try {
            const response = await runSiteMutation(dispatch, 'changeUserRole', model);

            if (response?.success === false) {
                throw { status: 400, data: { message: response.message } };
            }

            const responseRoleId = response.roleId ?? model.roleId ?? null;
            const responseRoleName = response.roleName ?? getRoleName(roles, responseRoleId, optimisticRoleName);

            dispatch(changeTableRow(table, buildUserRoleRow(item, responseRoleId, responseRoleName), 'userId'));
            //dispatch(getSiteHistory(model.siteId));

            if (isProfile) {
                dispatch(getSetting());
                dispatch(getUserPermissions());
            }

            if (response.message) {
                openSuccessNotification(response.message)
            } else {
                openSuccessNotification(`${item.contactName} role is changed`);
            }

            return response;
        }
        catch(error) {
            dispatch(changeTableRow(table, previousRow, 'userId'));

            if (!isUnauthorizedError(error)) {
                openErrorNotification(getApiErrorMessage(error, `Error. Can't change for ${item.contactName} role`));
            }

            throw error;
        }
        finally {
            setTimeout(() => {
                dispatch(setValue('siteDetailsLoading', false));
            }, config.LOADING_DELAY);
        }
    }
}

export const changeUsersRole = (items) => {
    return (dispatch) => {
        dispatch(setValue('siteDetailsLoading', true));
        
        // Create a map to track tables that need updating
        const tablesToUpdate = new Set();

        items.forEach(item => {
            // Get the correct tableId
            const tableId = item.tableId || `siteUsersTable-${item.model.siteId}`;
            tablesToUpdate.add(tableId);
            
            runSiteMutation(dispatch, 'changeUserRole', item.model)
                .then(response => {
                    if (response?.success === false) {
                        openErrorNotification(response.message || `Error. Can't change for ${item.item.contactName} role`);
                        return;
                    }

                    dispatch(changeTableRow(tableId, { 
                        ...item.item, 
                        roleId: response.roleId ?? item.model.roleId,
                        roleName: response.roleName,
                        role: response.roleName
                    }, 'userId'));
                    
                    //dispatch(getSiteHistory(response.data.siteId));
                    openSuccessNotification(`User Permissions Updated for ${item.item.contactName}`);

                    if (item.isProfile) {
                        dispatch(getSetting());
                        dispatch(getUserPermissions());
                    }
                })                
                .catch(error => {
                    if (!isUnauthorizedError(error)) {
                        openErrorNotification(getApiErrorMessage(error, `Error. Can't change for ${item.item.contactName} role`));
                    }
                })
                .finally(() => {
                    // Update the table after all individual requests are completed
                    const tableId = item.tableId || `siteUsersTable-${item.model.siteId}`;
                    dispatch(updateTable(tableId));
                    
                    setTimeout(() => {
                        dispatch(setValue('siteDetailsLoading', false));
                    }, config.LOADING_DELAY);
                })
        });
    }
}

export const getSiteUsers = (siteId) => {
    return (dispatch) => {
        runSiteQuery(dispatch, 'siteUsers', siteId)
            .then(data => {
                dispatch(setValue('siteSystemContacts', data));
            })
            .catch(error => {
                if (!isUnauthorizedError(error)) {
                    openErrorNotification("Can't load site users. Please reload page");
                }
            });
    }
}

export const getAccountSites = (accountId) => {
    return (dispatch) => {
        runSiteQuery(dispatch, 'getAccountSites', accountId)
            .then(data => {
                dispatch(setValue('accountSites', data));
                return Promise.resolve();
            })
            .catch(error => {
                if (!isUnauthorizedError(error)) {
                    openErrorNotification("Can't load sites. Please reload page");
                }
            });
    }
}

export const canApplySiteName = (dispatch, siteName, accountId, siteId) => {
    return runSiteQuery(dispatch, 'canApplySiteName', { siteName, accountId, siteId })
        .then(data => { return data })
        .catch(() => {
            return false;
        })
}

export const getSiteHistory = (siteId) => {
    return (dispatch) => {
        if (siteId) {
            runSiteMutation(dispatch, 'getSiteHistory', siteId)
                .then(data => {
                    dispatch(setHistory(data));
                })
                .catch(error => {
                    if (!isUnauthorizedError(error)) {
                        openErrorNotification(`Error. Can't refresh site history`);
                    }
                })
        }
    }
}

export default siteReducer;